import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Info } from "lucide-react";
import type { PatientConcern } from "@/types/appointments";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/consultation/EmptyState";

interface PatientConcernCardProps {
  patientConcerns: PatientConcern[];
  activeAppointmentId?: string;
  onManageConcern: () => void;
  variant?: "default" | "panel";
  className?: string;
  isCompleted?: boolean;
}

export const PatientConcernCard = ({
  patientConcerns,
  activeAppointmentId,
  onManageConcern,
  variant = "panel",
  className,
  isCompleted = false,
}: PatientConcernCardProps) => {
  return (
    <Card
      className={cn(
        "rounded-3xl border-border/60 bg-card shadow-sm",
        className
      )}
    >
      <CardHeader className="p-4 flex flex-row items-center justify-between border-b border-border/60">
        <div className="flex items-center gap-2">
          <CardTitle className="z-h3 text-foreground">
            Patient Concern
          </CardTitle>
          <Info className="size-4 text-muted-foreground" />
        </div>
        {!isCompleted && (
          <Button
            size="icon"
            className="h-9 w-9 rounded-xl"
            onClick={onManageConcern}
          >
            <Plus className="size-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-4">
        {(() => {
          const filteredConcerns = patientConcerns
            .filter(
              (concern) =>
                concern.appointment === activeAppointmentId ||
                !activeAppointmentId
            )
            .flatMap((concern) => concern.concerns);

          return filteredConcerns.length > 0 ? (
            <div className="space-y-1 max-h-[120px] overflow-y-auto scrollbar-thin">
              {filteredConcerns.map((concern, idx) => (
                <div
                  key={idx}
                  className="bg-muted/40 rounded-lg py-1.5 px-2.5 flex items-start gap-2"
                >
                  <span className="z-body-md text-muted-foreground shrink-0">
                    {idx + 1}.
                  </span>
                  <p className="z-body text-foreground leading-tight">
                    {concern}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No concerns reported" variant="compact" />
          );
        })()}
      </CardContent>
    </Card>
  );
};
